import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { SHIPMENT_TOAST_MESSAGES } from "~/features/shipment-management/constants/toast-messages.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export const postMarkUndelivered = async (poNumber: string) => {
	const resp = await postAndValidate(
		apiRoutes.POSTMARKUNDELIVERED.replace(":poNumber", poNumber),
		null,
		z.unknown(),
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostMarkUndelivered = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationKey: ["markUndelivered"],
		mutationFn: (poNumber: string) => postMarkUndelivered(poNumber),
		// Awaiting the refetch keeps the mutation pending (and the spinner up)
		// until the shipment reflects the new state; toast once it's done.
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess(SHIPMENT_TOAST_MESSAGES.shipmentUnmarkedDelivered);
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to unmark shipment as delivered. Please try again.",
			);
		},
	});

	return {
		markUndelivered: mutate,
		markUndeliveredAsync: mutateAsync,
		isSavingMarkUndelivered: isPending,
		error,
		data,
		resetError: reset,
	};
};
